import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { useParams, useLocation, Link } from "react-router-dom";
import { Printer, ArrowRight, Coins, Copy, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

function describeTokenStatus(submission) {
  if (submission.redeemed_at) {
    const date = new Date(submission.redeemed_at).toLocaleDateString();
    return `Redeemed · ${date}`;
  }
  if (submission.status === "approved") return "Approved — on the Wall";
  if (submission.status === "flagged") return "Pending safety review (auto-flagged)";
  if (submission.status === "rejected") return "Rejected";
  return "Pending review";
}

export default function Confirmation() {
  const { id } = useParams();
  const location = useLocation();
  const [submission, setSubmission] = useState(location.state?.submission || null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!submission && id) {
      api
        .get(`/submissions/${id}`)
        .then((r) => setSubmission(r.data))
        .catch((err) => logger.error("[confirmation] load failed", err));
    }
  }, [id, submission]);

  const handleCopy = async () => {
    if (!submission?.token_code) return;
    try {
      await navigator.clipboard.writeText(submission.token_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.warn("[confirmation] clipboard write failed", err);
    }
  };

  if (!submission) {
    return (
      <div className="bg-white min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center ws-label">Loading…</div>
        <SiteFooter />
      </div>
    );
  }

  const photoUrl = submission.photo_url;
  const issuedDate = new Date(submission.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="bg-white text-[#09090B] min-h-screen print:bg-white">
      <div className="print:hidden">
        <SiteHeader />
      </div>

      <section className="border-b-2 border-[#09090B] bg-[#09090B] text-white print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
          <div className="ws-label text-[#FFCC00]">/ Confirmation</div>
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter mt-3 leading-[0.95]">
            STORY RECEIVED.<br />
            <span className="text-[#FFCC00]">TOKEN ISSUED.</span>
          </h1>
          <p className="mt-4 text-[#E4E4E7] max-w-xl">
            Show or screenshot this token at the company store to redeem your reward. Your story is
            in queue for the Wall of Safety after a quick review.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* Token "ticket" */}
        <article
          data-testid="safety-token"
          className="grid md:grid-cols-[1fr_auto_2fr] border-2 border-[#09090B] ws-hard-shadow overflow-hidden bg-white"
        >
          {/* Left stub */}
          <div className="ws-ticket p-8 flex flex-col justify-between text-[#09090B]">
            <div className="ws-label flex items-center gap-2">
              <Coins strokeWidth={2.5} className="w-4 h-4" />
              Safety Token
            </div>
            <div className="mt-12">
              <div className="text-xs uppercase tracking-[0.2em] font-bold">Value</div>
              <div className="font-display font-black text-5xl tracking-tighter mt-1">01</div>
              <div className="ws-label mt-1">Company Store</div>
            </div>
            <div className="font-display font-black text-2xl mt-12 tracking-tighter">★</div>
          </div>

          {/* Perforation */}
          <div className="hidden md:block w-px bg-transparent relative">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px ws-dotted-edge" />
          </div>

          {/* Right body */}
          <div className="p-8 bg-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="ws-label text-[#52525B]">Issued to</div>
                <div className="font-display font-bold text-2xl tracking-tight mt-1" data-testid="token-employee">
                  {submission.employee_name}
                </div>
                <div className="text-sm text-[#52525B] mt-1">
                  ID · {submission.employee_id} · {issuedDate}
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-3 py-1 ws-label border-2 border-[#09090B]">
                <CheckCircle2 strokeWidth={2.5} className="w-4 h-4" /> {submission.redeemed_at ? "Redeemed" : "Valid"}
              </div>
            </div>

            <div className="mt-6 border-2 border-[#09090B] bg-[#F4F4F5]">
              <div className="px-4 py-2 ws-label bg-[#09090B] text-white">Redemption Code</div>
              <div className="px-4 py-5 flex items-center justify-between gap-3 flex-wrap">
                <div
                  className="font-mono font-bold text-2xl sm:text-3xl tracking-widest text-[#09090B]"
                  data-testid="token-code"
                >
                  {submission.token_code}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="copy-token-btn"
                  className="inline-flex items-center gap-2 bg-[#09090B] text-white ws-label px-4 py-2 hover:bg-[#FF5A00] transition-colors"
                >
                  {copied ? <CheckCircle2 strokeWidth={2.5} className="w-4 h-4" /> : <Copy strokeWidth={2.5} className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="ws-label text-[#52525B]">Status</div>
                <div className="mt-1 font-semibold" data-testid="token-status">
                  {describeTokenStatus(submission)}
                </div>
              </div>
              <div>
                <div className="ws-label text-[#52525B]">Reason captured</div>
                <div className="mt-1 text-[#09090B] leading-snug line-clamp-3">{submission.reason}</div>
              </div>
            </div>
          </div>
        </article>

        {/* Photo + actions */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="border-2 border-[#09090B] ws-hard-shadow overflow-hidden bg-[#F4F4F5]">
            <img src={photoUrl} alt="Your why" className="w-full h-full max-h-[420px] object-cover" />
          </div>
          <div className="space-y-4">
            <div className="border-2 border-[#09090B] bg-white p-6">
              <div className="ws-label">Your story</div>
              <p className="mt-3 text-[#09090B] text-base leading-relaxed">{submission.reason}</p>
            </div>
            <div className="flex flex-wrap gap-3 print:hidden">
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="print-token-btn"
                className="inline-flex items-center gap-2 bg-[#09090B] text-white font-bold px-6 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow hover:bg-[#27272A] transition-colors"
              >
                <Printer strokeWidth={2.5} className="w-4 h-4" /> Print Token
              </button>
              <Link
                to="/wall"
                data-testid="view-wall-link"
                className="inline-flex items-center gap-2 bg-white text-[#09090B] font-bold px-6 py-3 ws-label border-2 border-[#09090B] hover:bg-[#F4F4F5] transition-colors"
              >
                Visit the Wall <ArrowRight strokeWidth={2.5} className="w-4 h-4" />
              </Link>
              <Link
                to="/submit"
                data-testid="submit-another-link"
                className="inline-flex items-center gap-2 text-[#FF5A00] font-bold px-6 py-3 ws-label hover:underline"
              >
                Submit Another →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="print:hidden">
        <SiteFooter />
      </div>
    </div>
  );
}
